/* eslint-disable react/no-unused-prop-types -- these props are used in the Wizard component */
import {
  Children,
  cloneElement,
  type ComponentType,
  type FC,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react'

type StepProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>
  name: string
  optional?: boolean
}

type Props = PropsWithChildren<StepProps>

type StepGroupProps = PropsWithChildren<{
  optional?: boolean
  disabled?: boolean
}>

type BranchProps = PropsWithChildren<{
  stateKey: string
  optional?: boolean
}>

type BranchPathProps = PropsWithChildren<{
  value: string
}>

export type InternalStepProps = Props & {
  group: string
  disabled?: boolean
  branchKey?: string
  branchValue?: string
}

type StepElementsArray = ReactElement<InternalStepProps>[]

export const Step: FC<Props> = ({ component: Component }) => <Component />

export const StepGroup: FC<StepGroupProps> = ({ children }) => children

export const Branch: FC<BranchProps> = ({ children }) => children

export const BranchPath: FC<BranchPathProps> = ({ children }) => children

export const convertChildrenToSteps = (children: ReactNode): StepElementsArray =>
  (Children.toArray(children) as StepElementsArray).reduce<StepElementsArray>((steps, child) => {
    const { children: childrenOfChild, component: Component, disabled, ...rest } = child.props

    if (disabled) {
      return steps
    }

    // Handle Branch component: annotate children with branchKey/branchValue
    const { stateKey } = child.props as unknown as BranchProps
    if (stateKey && childrenOfChild) {
      const branchOptional = (child.props as unknown as BranchProps).optional
      const branchPaths = Children.toArray(childrenOfChild) as ReactElement<BranchPathProps>[]
      const branchGroup = `group${child.key}`

      return [
        ...steps,
        ...branchPaths.reduce<StepElementsArray>((branchSteps, pathChild) => {
          const pathValue = pathChild.props.value
          const pathChildren = Children.toArray(pathChild.props.children) as StepElementsArray

          return [
            ...branchSteps,
            ...pathChildren.map((stepChild) =>
              cloneElement(stepChild, {
                group: branchGroup,
                branchKey: stateKey,
                branchValue: pathValue,
                ...(branchOptional ? { optional: true } : {}),
              })
            ),
          ]
        }, []),
      ]
    }

    // Handle StepGroup: recursively process children and propagate group/optional
    if (childrenOfChild) {
      const groupKey = `group${child.key}`
      const processedChildren = convertChildrenToSteps(childrenOfChild)
      return [...steps, ...processedChildren.map((c) => cloneElement(c, { ...rest, group: groupKey }))]
    }

    if (Component) {
      return [...steps, <Component {...child.props} group={`group${child.key}`} key={`group${child.key}`} />]
    }

    return steps
  }, [])
